import { Component, OnDestroy, OnInit, Input, Optional } from '@angular/core';
import {
    ControlContainer,
    FormControl,
    FormGroupDirective,
} from '@angular/forms';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { fromEvent, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CheckoutFormService } from '../../checkout-form-service/checkout-form.service';
import { ConfigService } from 'src/app/services/config/config.service';
import { CreditCardProcessingService } from 'src/app/shared/credit-card-processing/credit-card-processing.service';
import { CreditCardProcessingMethod } from 'src/app/shared/credit-card-processing-method.enum';

@Component({
    selector: 'ols-credit-card-information',
    templateUrl: './credit-card-information.component.html',
    styleUrls: ['./credit-card-information.component.scss'],
    viewProviders: [
        { provide: ControlContainer, useExisting: FormGroupDirective },
    ],
})
export class CreditCardInformationComponent implements OnInit, OnDestroy {
    @Input() surchargeSettings: {
        surchargeEnabled: boolean;
        surchargeRate: number;
    };
    @Input() summary: any;
    creditCardTokenizerUrl: SafeResourceUrl;
    isCreditCardIframeLoaded = false;
    isStripeEnabled = false;
    currencyCode = 'usd';

    private ngUnsubscribe: Subject<any> = new Subject();

    get creditCardToken(): FormControl {
        return this.formDirective?.form?.get('creditCardToken') as FormControl;
    }
    get creditCardExpiration(): FormControl {
        return this.formDirective?.form?.get(
            'creditCardExpiration'
        ) as FormControl;
    }
    get creditCardCvv(): FormControl {
        return this.formDirective?.form?.get('creditCardCvv') as FormControl;
    }
    get creditCardZip(): FormControl {
        return this.formDirective?.form?.get('creditCardZip') as FormControl;
    }
    get creditCardClientName(): FormControl {
        return this.formDirective?.form?.get(
            'creditCardClientName'
        ) as FormControl;
    }

    constructor(
        private sanitizer: DomSanitizer,
        @Optional() private formDirective: FormGroupDirective,
        private formService: CheckoutFormService,
        private readonly configService: ConfigService,
        private creditCardProcessingService: CreditCardProcessingService
    ) {}
    ngOnInit(): void {
        const processingMethod = this.creditCardProcessingService.getCreditCardProcessingMethod();
        this.isStripeEnabled =
            processingMethod === CreditCardProcessingMethod.Stripe;

        if (!this.isStripeEnabled) {
            this.initCardConnect();
        }
    }

    submitForm() {
        this.formService.submitForm();
    }

    creditCardIframeLoaded() {
        this.isCreditCardIframeLoaded = true;
    }

    private initCardConnect() {
        this.creditCardTokenizerUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
            encodeURI(
                `https://daysmart${
                    this.configService.config.production ? '' : '-uat'
                }.cardconnect.com/itoke/ajax-tokenizer.html` +
                    this.generateCardConnectCssUrlParams() +
                    '&invalidinputevent=true&tokenizewheninactive=true&inactivityto=3000&placeholder=Card Number&formatinput=true'
            )
        );

        this.listenForCardConnectEvents();
    }

    private listenForCardConnectEvents() {
        fromEvent(window, 'message')
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((event: MessageEvent) => {
                // Only catch responses from cardconnect
                const targets = [
                    'daysmart.cardconnect.com',
                    'daysmart-uat.cardconnect.com',
                ];
                if (targets.some((t) => event?.origin.includes(t))) {
                    if (event.data) {
                        try {
                            const data = JSON.parse(event.data);
                            this.creditCardToken.markAsTouched();

                            if (data.message) {
                                const tokenNumber = data.message;
                                this.creditCardToken.setValue(tokenNumber);
                                this.creditCardToken.setErrors(null);
                            } else {
                                this.creditCardToken.setErrors({
                                    invalidCreditCardNum: true,
                                });
                            }
                        } catch (err) {
                            console.error(err);
                        }
                    }
                }
            });
    }

    private generateCardConnectCssUrlParams() {
        return (
            `?css=body{margin: 0;}` +
            `input{box-sizing: border-box;outline: none;width: 100%;font-size: 16px;` +
            `padding: 7px 0;border-style:none;font-family: Arial, sans-serif}`
        );
    }

    ngOnDestroy() {
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
    }

    shouldShowSurchargeBanner(): boolean {
        return (
            this.surchargeSettings?.surchargeEnabled &&
            this.surchargeSettings?.surchargeRate > 0 &&
            this.creditCardProcessingService.getCreditCardProcessingMethod() ===
                CreditCardProcessingMethod.Stripe
        );
    }
}
